import { useEffect, useState } from 'preact/hooks'
import { hubspotApi } from '@/api/hubspot'
import type { HubSpotDealAnalytics } from '@/types/hubspot'

interface DealAnalyticsState {
    data: HubSpotDealAnalytics | null
    loading: boolean
    error: string | null
}

/**
 * Shared fetch/state for the deal-analytics endpoints. Re-fetches whenever `scopeKey` changes;
 * `fetcher` is read at call time so it always uses the latest scope ids.
 */
function useDealAnalytics(
    scopeKey: string | null,
    fetcher: () => Promise<{ data?: HubSpotDealAnalytics | null; error?: string | null }>,
): DealAnalyticsState {
    const [data, setData] = useState<HubSpotDealAnalytics | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        // No scope (e.g. workspace deselected, or scope changed away mid-request): clear state so the
        // UI never gets stuck on a stale `loading` from a now-cancelled fetch.
        if (!scopeKey) {
            setData(null)
            setError(null)
            setLoading(false)
            return
        }
        let cancelled = false
        const load = async () => {
            setLoading(true)
            setError(null)
            const resp = await fetcher()
            if (!cancelled) {
                if (resp.error) setError(resp.error)
                else setData(resp.data || null)
                setLoading(false)
            }
        }
        load()
        return () => { cancelled = true }
        // `scopeKey` fully captures the scope; fetcher is intentionally excluded.
    }, [scopeKey])

    return { data, loading, error }
}

/** Deal attribution analytics for a workspace (deduped across its campaigns). */
export function useWorkspaceDealAnalytics(workspaceId: string) {
    return useDealAnalytics(workspaceId || null, () => hubspotApi.getWorkspaceDealAnalytics(workspaceId))
}

/** Deal attribution analytics for a single campaign (its target-list companies). */
export function useCampaignDealAnalytics(workspaceId: string, campaignId: string) {
    return useDealAnalytics(
        workspaceId && campaignId ? `${workspaceId}/${campaignId}` : null,
        () => hubspotApi.getCampaignDealAnalytics(workspaceId, campaignId),
    )
}
